import { Observable } from 'rxjs';
import zipState from 'zip-state';
import { Store } from '@ngxs/store';
import {
  DeclineAutoPaymentV4,
  DeclinePaymentV3,
  DeclineRecurringPaymentV4,
  ResumeAutoPaymentV4,
  ResumePaymentV3,
  ResumeRecurringPaymentV4,
  UpdatePaymentV3,
  UpdatePaymentV4,
} from '../../settings/state/settings.action';
import { Client, ContactInfo, CountryAlpha2Code, DateService, UsStateAbbreviation } from '@aiwyn/shared';
import {
  ActionDefInstanceStaged,
  EditScheduledPaymentPayload,
  EngagementScheduledPaymentModel,
  HasuraScheduledPaymentModel,
  PaymentAction,
  PaymentActionDefInstanceDTO,
  PaymentPlan,
  ScheduledPaymentUiModel,
} from '@aiwyn/api';
import { Router } from '@angular/router';
import { ROUTE_TOKEN_ENGAGEMENTS_BASE, ROUTE_TOKEN_MAIN_BASE } from 'src/app/routing-constants';

/**
 * Restructure old data class to be an abstract base class that inherits the new model, except with getters and setters that can be overridden
 */
export abstract class ScheduledPaymentUi implements ScheduledPaymentUiModel {
  private _paymentPlan: PaymentPlan;
  get paymentPlan(): PaymentPlan {
    return this._paymentPlan;
  }
  set paymentPlan(plan: PaymentPlan) {
    this._paymentPlan = { ...plan };
  }

  private _actionDefInstanceDTO: PaymentActionDefInstanceDTO;
  get actionDefInstanceDTO(): PaymentActionDefInstanceDTO {
    return this._actionDefInstanceDTO;
  }
  set actionDefInstanceDTO(action: PaymentActionDefInstanceDTO) {
    this._actionDefInstanceDTO = { ...action };
    this._actionDefInstanceDTO.createdAt = DateService.getLocalTime(this._actionDefInstanceDTO.createdAt);
    this._actionDefInstanceDTO.updatedAt = DateService.getLocalTime(this._actionDefInstanceDTO.updatedAt);
  }

  private _actionDefInstanceStaged: ActionDefInstanceStaged;
  get actionDefInstanceStaged(): ActionDefInstanceStaged {
    return this._actionDefInstanceStaged;
  }
  set actionDefInstanceStaged(action: ActionDefInstanceStaged) {
    this._actionDefInstanceStaged = { ...action };
    this._actionDefInstanceStaged.createdAt = DateService.getLocalTime(this._actionDefInstanceStaged.createdAt);
    this._actionDefInstanceStaged.updatedAt = DateService.getLocalTime(this._actionDefInstanceStaged.updatedAt);
  }

  private _client: Client;
  get client(): Client {
    return this._client;
  }
  set client(client: Client) {
    const clientCopy = { ...client };
    clientCopy.updatedAt = DateService.getLocalTime(client.updatedAt);
    clientCopy.createdAt = DateService.getLocalTime(client.createdAt);
    this._client = clientCopy;
  }

  private _contactInfo: ContactInfo;
  get contactInfo(): ContactInfo {
    return this._contactInfo;
  }
  set contactInfo(info: ContactInfo) {
    this._contactInfo = { ...info };
  }

  private _isPaymentMethodFromUser: boolean;
  get isPaymentMethodFromUser(): boolean {
    return this._isPaymentMethodFromUser;
  }
  set isPaymentMethodFromUser(isFromUser: boolean) {
    this._isPaymentMethodFromUser = isFromUser;
  }

  private _optOutOfProcessingFees: boolean;
  get optOutOfProcessingFees(): boolean {
    return this._optOutOfProcessingFees;
  }
  set optOutOfProcessingFees(optOut: boolean) {
    this._optOutOfProcessingFees = optOut;
  }

  protected constructor() {
    this.optOutOfProcessingFees = false;
  }

  get paymentMethodCountry(): CountryAlpha2Code {
    if (this.paymentPlan.country && this.paymentPlan.country.trim() !== '') {
      return this.paymentPlan.country as CountryAlpha2Code;
    } else if (this.paymentPlan.paymentMethod === 'ACSS') {
      return CountryAlpha2Code.CANADA;
    }

    // Either payment method is an ACH or has no country, so default to US
    return CountryAlpha2Code.UNITED_STATES;
  }

  getAmountDisplay(..._: unknown[]): string | number {
    return this.actionDefInstanceDTO.action.amount;
  }

  showMaximumAmount(): boolean {
    return false;
  }

  getMaximumAmount(): number {
    return undefined;
  }

  getDisplayName(): string {
    return this.paymentPlan.name;
  }

  canNavigateToDetailedPage(): boolean {
    return false;
  }

  getNavigationMessage(): string | undefined {
    return;
  }

  navigateToPage(router: Router): void {}

  abstract canEdit(): boolean;

  // Use manual dependency injection to pass in the payments service in order to abstract out the differences between V3, V4, and Autopay
  abstract getDeclineRequest(store: Store): Observable<unknown>;
  abstract getResumeRequest(store: Store): Observable<unknown>;
  abstract getUpdateRequest(
    store: Store,
    contactId: number,
    createdByStaffId: number,
    cardType: string,
    last4: string,
    stripeUserId: string,
    stripeConnectedAccountId: string,
    paymentMethodId: string,
    paymentMethod: string,
    amount: number,
    zip: string,
    stateAbbreviation: string,
    country: string,
    scheduledReleaseDate: Date,
    updatedByStaffId: number,
    updatedByContactId: number,
  ): Observable<unknown>;
}

/**
 * V3 implementation will accept a model, and just use the inherited getters and setters
 */
export class ScheduledPaymentUiV3 extends ScheduledPaymentUi{

  constructor(model: ScheduledPaymentUiModel) {
    super();
    // Use inherited setters
    this.paymentPlan = model.paymentPlan;
    this.actionDefInstanceDTO = model.actionDefInstanceDTO;
    this.actionDefInstanceStaged = model.actionDefInstanceStaged;
    this.client = model.client;
    this.contactInfo = model.contactInfo;
    this.isPaymentMethodFromUser = model.isPaymentMethodFromUser;
    this.optOutOfProcessingFees = model.optOutOfProcessingFees;
  }

  canEdit(): boolean {
    return true;
  }

  getDeclineRequest(store: Store): Observable<unknown> {
    return store.dispatch(new DeclinePaymentV3(this.actionDefInstanceDTO.id));
  }

  getResumeRequest(store: Store): Observable<unknown> {
    return store.dispatch(new ResumePaymentV3(this.actionDefInstanceDTO.id));
  }

  getUpdateRequest(
    store: Store,
    contactId: number,
    createdByStaffId: number,
    cardType: string,
    last4: string,
    stripeUserId: string,
    stripeConnectedAccountId: string,
    paymentMethodId: string,
    paymentMethod: string,
    amount: number,
    zip: string,
    stateAbbreviation: string,
    country: string,
    scheduledReleaseDate: Date,
    updatedByStaffId: number,
    updatedByContactId: number,
  ): Observable<unknown> {
    const editedScheduledPaymentPayload: EditScheduledPaymentPayload = {
      paymentAction: {
        stage: false,
        reviewer: null,
        canScheduleReleaseDate: false,

        clientId: this.actionDefInstanceDTO.action.clientId,
        contactId,
        staffId: createdByStaffId,
        cardType,
        last4,
        stripeUserId,
        stripeConnectedAccountId,
        paymentMethodId,
        paymentMethod,
        amount,
        zip,
        stateAbbreviation,
        country,
      },
      actionDefInstanceId: this.actionDefInstanceDTO.id,
      scheduledReleaseDate,
      staffId: updatedByStaffId,
    };

    return store.dispatch(new UpdatePaymentV3(editedScheduledPaymentPayload));
  }
}

/**
 * Hasura model is radically different, and will just back-fill the getters from the new model. Setters will not work
 */
export abstract class HasuraScheduledPaymentUi extends ScheduledPaymentUi {
  model: HasuraScheduledPaymentModel;

  get paymentPlan(): PaymentPlan {
    return {
      name: this.model.name,
      amount: this.model.amount || this.model.maxAmount,
      startDate: this.model.startDate,
      endDate: this.model.endDate,
      frequency: this.model.frequency,
      createdByContactId: this.model.createdByContactId,
      createdByStaffId: this.model.createdByStaffId,
      clientId: this.model.clientId,
      paymentMethodId: this.model.platformPaymentMethodId,
      paymentMethod: this.model.paymentMethodType,
      last4: this.model.last4,
      cardType: this.model.cardType,
      zip: this.model.zip,
      country: this.model.country,
      emailConfirmationAddressList: this.model.scheduledPaymentEmails,
    } as unknown as PaymentPlan;
  }
  set paymentPlan(plan: PaymentPlan) {
    throw new Error('Cannot use setter in child implementation');
  }

  get actionDefInstanceDTO(): PaymentActionDefInstanceDTO {
    return {
      action: {
        clientId: this.model.clientId,
        contactId: this.model.createdByContactId,
        staffId: this.model.createdByStaffId,
        cardType: this.model.cardType,
        last4: this.model.last4,
        paymentMethodId: this.model.platformPaymentMethodId,
        paymentMethod: this.model.paymentMethodType,
        amount: this.model.amount || this.model.maxAmount,
        zip: this.model.zip,
        stateAbbreviation: this.model.zip ? zipState(this.model.zip) : undefined,
        country: this.model.country
      } as PaymentAction,
      scheduledReleaseDate: this.model.releaseDate,
      status: this.model.active ? 'STAGED' : 'DECLINED',
    } as unknown as PaymentActionDefInstanceDTO;
  }
  set actionDefInstanceDTO(action: PaymentActionDefInstanceDTO) {
    throw new Error('Cannot use setter in child implementation');
  }

  // Unknown cast to circumvent warnings. Model is purely for display
  get actionDefInstanceStaged(): ActionDefInstanceStaged {
    return {
      status: this.model.status,
      scheduledReleaseDate: this.model.releaseDate,
    } as unknown as ActionDefInstanceStaged;
  }
  set actionDefInstanceStaged(action: ActionDefInstanceStaged) {
    throw new Error('Cannot use setter in child implementation');
  }

  get client(): Client {
    return {
      id: this.model.clientId,
      name: this.model.clientName,
    } as Client;
  }
  set client(client: Client) {
    throw new Error('Cannot use setter in child implementation');
  }

  get contactInfo(): ContactInfo {
    return {
      fullName: this.model.staffFullName || this.model.contactFullName,
    } as ContactInfo;
  }
  set contactInfo(info: ContactInfo) {
    throw new Error('Cannot use setter in child implementation');
  }

  constructor(model: HasuraScheduledPaymentModel) {
    super();
    this.model = model;
    this.optOutOfProcessingFees = model.optOutOfProcessingFees;
  }

  showMaximumAmount(): boolean {
    return !!(this.model.maxAmount && !this.model.amount);
  }

  getMaximumAmount(): number {
    return this.model.maxAmount;
  }

  getAmountDisplay(..._: unknown[]): string | number {
    return this.model.amount;
  }

  /**
   * Static helper function to create a child instance based off the model
   * @param model
   */
  static create(model: HasuraScheduledPaymentModel): HasuraScheduledPaymentUi {
    if (model.type === 'RECURRING') {
      return new ScheduledRecurringPaymentUiV4(model);
    }

    return model.scheduledPaymentId ?
      new ScheduledAutoPaymentUiV4(model) :
      new BufferedScheduledAutoPaymentUiV4(model);
  }
}

/**
 * Engagement model implementation similar to Hasura model
 */
export class EngagementScheduledPaymentUi extends ScheduledPaymentUi {
  model: EngagementScheduledPaymentModel;

  get paymentPlan(): PaymentPlan {
    return {
      name: this.model.engagementName,
      amount: this.model.amount,
      clientId: this.model.clientId,
      paymentMethodId: this.model.platformPaymentMethodId,
      paymentMethod: this.model.paymentMethodType,
      last4: this.model.paymentMethodLast4,
      cardType: this.model.paymentMethodCardType,
      zip: this.model.paymentMethodZip,
      country: this.model.paymentMethodCountry,
    } as unknown as PaymentPlan;
  }
  set paymentPlan(plan: PaymentPlan) {
    throw new Error('Cannot use setter in child implementation');
  }

  get actionDefInstanceDTO(): PaymentActionDefInstanceDTO {
    return {
      action: {
        clientId: this.model.clientId,
        cardType: this.model.paymentMethodCardType,
        last4: this.model.paymentMethodLast4,
        paymentMethodId: this.model.platformPaymentMethodId,
        paymentMethod: this.model.paymentMethodType,
        amount: this.model.amount,
        zip: this.model.paymentMethodZip,
        stateAbbreviation: this.model.paymentMethodZip ? zipState(this.model.paymentMethodZip) : undefined,
        country: this.model.paymentMethodCountry
      } as PaymentAction,
      scheduledReleaseDate: this.model.date,
      status: this.model.billingScheduleActive ? 'STAGED' : 'DECLINED',
    } as unknown as PaymentActionDefInstanceDTO;
  }
  set actionDefInstanceDTO(action: PaymentActionDefInstanceDTO) {
    throw new Error('Cannot use setter in child implementation');
  }

  get actionDefInstanceStaged(): ActionDefInstanceStaged {
    return {
      status: this.model.billingScheduleActive ? 'ACTIVE' : 'INACTIVE',
      scheduledReleaseDate: this.model.date,
    } as unknown as ActionDefInstanceStaged;
  }
  set actionDefInstanceStaged(action: ActionDefInstanceStaged) {
    throw new Error('Cannot use setter in child implementation');
  }

  get client(): Client {
    return {
      id: this.model.clientId,
      name: this.model.clientName,
    } as Client;
  }
  set client(client: Client) {
    throw new Error('Cannot use setter in child implementation');
  }

  get contactInfo(): ContactInfo {
    return {
      fullName: this.model.clientName,
    } as ContactInfo;
  }
  set contactInfo(info: ContactInfo) {
    throw new Error('Cannot use setter in child implementation');
  }

  constructor(model: EngagementScheduledPaymentModel) {
    super();
    this.model = model;
    this.optOutOfProcessingFees = false;
  }

  canEdit(): boolean {
    return false;
    // return this.model.billingScheduleActive;
  }

  canNavigateToDetailedPage(): boolean {
    return true;
  }

  getNavigationMessage(): string | undefined {
    return 'Open engagement';
  }

  navigateToPage(router: Router): void {
    router.navigate([ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_ENGAGEMENTS_BASE, this.model.engagementId]);
  }

  getDeclineRequest(store: Store): Observable<unknown> {
    throw new Error('Decline operation not implemented for engagement scheduled payments');
  }

  getResumeRequest(store: Store): Observable<unknown> {
    throw new Error('Resume operation not implemented for engagement scheduled payments');
  }

  getUpdateRequest(
    store: Store,
    contactId: number,
    createdByStaffId: number,
    cardType: string,
    last4: string,
    stripeUserId: string,
    stripeConnectedAccountId: string,
    paymentMethodId: string,
    paymentMethod: string,
    amount: number,
    zip: string,
    stateAbbreviation: string,
    country: string,
    scheduledReleaseDate: Date,
    updatedByStaffId: number,
    updatedByContactId: number,
  ): Observable<unknown> {
    throw new Error('Update operation not implemented for engagement scheduled payments');
  }

  getDisplayName(): string {
    return `${this.model.engagementName} - ${this.model.jobName}`;
  }

  getAmountDisplay(..._: unknown[]): string | number {
    return this.model.amount;
  }
}

/**
 * Abstracted commonalities between schedule payments that are either auto or recurring, but do not necessarily belong to the hasura class
 */
abstract class ScheduledPaymentUiV4 extends HasuraScheduledPaymentUi {

  getDeclineRequest(store: Store): Observable<unknown> {
    return store.dispatch(new DeclineRecurringPaymentV4(this.model.scheduledPaymentId));
  }

  getResumeRequest(store: Store): Observable<unknown> {
    return store.dispatch(new ResumeRecurringPaymentV4(this.model.scheduledPaymentId));
  }
}

export class ScheduledRecurringPaymentUiV4 extends ScheduledPaymentUiV4 {

  /**
   * All non-inferred (inferred TBD) scheduled payments can be edited.
   */
  canEdit(): boolean {
    return true;
  }

  getUpdateRequest(
    store: Store,
    contactId: number,
    createdByStaffId: number,
    cardType: string,
    last4: string,
    stripeUserId: string,
    stripeConnectedAccountId: string,
    paymentMethodId: string,
    paymentMethod: string,
    amount: number,
    zip: string,
    stateAbbreviation: UsStateAbbreviation,
    country: string,
    scheduledReleaseDate: Date,
    updatedByStaffId: number,
    updatedByContactId: number,
  ): Observable<unknown> {
    return store.dispatch(new UpdatePaymentV4({
      id: this.model.scheduledPaymentId,
      useCaseInstanceId: this.model.useCaseInstanceId,
      automaticPaymentPlanId: this.model.paymentPlanId,
      paymentMethodId,
      paymentMethod,
      last4,
      zip,
      country,
      stateAbbreviation,
      cardType,
      releaseDt: `${scheduledReleaseDate.getFullYear()}-${(scheduledReleaseDate.getMonth() + 1).toString().padStart(2, '0')}-${scheduledReleaseDate.getDate().toString().padStart(2, '0')}`,
      amount,
      createdByContactId: this.model.createdByContactId ? this.model.createdByContactId : undefined,
      createdByStaffId: this.model.createdByStaffId ? this.model.createdByStaffId : undefined,
      lastUpdatedByStaffId: updatedByStaffId ? updatedByStaffId : undefined,
      lastUpdatedByContactId: updatedByContactId ? updatedByContactId : undefined,
      emailConfirmationAddressList: this.model.scheduledPaymentEmails,
    }));
  }

  getAmountDisplay(
    paymentPlanProcessingFeeRate: number,
    internationFeeRate: number,
    firmCountry: CountryAlpha2Code,
  ): string | number {
    if (paymentPlanProcessingFeeRate == undefined && internationFeeRate == undefined && firmCountry == undefined) {
      return super.getAmountDisplay();
    }

    const processingFee = this.model.amount * (paymentPlanProcessingFeeRate ?? 0);
    const internationalFee = this.model.amount * (internationFeeRate ?? 0);
    const total = this.model.amount
      + (this.model.paymentMethodType === 'CREDIT' && !this.model.optOutOfProcessingFees ? processingFee : 0)
      + (firmCountry != undefined && this.paymentMethodCountry !== firmCountry ? internationalFee : 0);

    return +total.toFixed(2);
  }
}

export class ScheduledAutoPaymentUiV4 extends ScheduledPaymentUiV4 {

  getDisplayName(): string {
    return this.model.maxAmount && !this.model.amount ?
      'AutoPay outstanding invoices up to ' :
      'AutoPay outstanding invoices';
  }

  /**
   * No AutoPayments can be edited
   */
  canEdit(): boolean {
    return false;
  }

  /**
   * Cannot edit AutoPayments, throw error (Blame the engineer).
   */
  getUpdateRequest(
    store: Store,
    contactId: number,
    createdByStaffId: number,
    cardType: string,
    last4: string,
    stripeUserId: string,
    stripeConnectedAccountId: string,
    paymentMethodId: string,
    paymentMethod: string,
    amount: number,
    zip: string,
    stateAbbreviation: string,
    country: string,
    scheduledReleaseDate: Date,
    updatedByStaffId: number,
    updatedByContactId: number,
  ): Observable<unknown> {
    throw new Error('Cannot edit an AutoPay scheduled payment yOu SiLlY gOoSe');
  }

  getAmountDisplay(
    paymentPlanProcessingFeeRate: number,
    internationFeeRate: number,
    firmCountry: CountryAlpha2Code,
  ): string | number {
    if (paymentPlanProcessingFeeRate == undefined && internationFeeRate == undefined && firmCountry == undefined) {
      return super.getAmountDisplay();
    }

    // if amount === max amount, return max amount
    if (this.model.amount === this.model.maxAmount) {
      return this.model.maxAmount;
    }

    // if amount is less than max amount, return amount + fees (international, processing)
    const processingFee = this.model.amount * (paymentPlanProcessingFeeRate ?? 0);
    const internationalFee = this.model.amount * (internationFeeRate ?? 0);
    const total = this.model.amount
      + (this.model.paymentMethodType === 'CREDIT' && !this.model.optOutOfProcessingFees ? processingFee : 0)
      + (firmCountry != undefined && this.paymentMethodCountry !== firmCountry ? internationalFee : 0);

    // use minimum of max/total so that we don't display a value greater than the set limit
    return +(Math.min(total, this.model.maxAmount)).toFixed(2);
  }
}

/**
 * AutoPayments that have not yet reached their buffer days and are not scheduled (SLIGHTLY different functionality).
 */
export class BufferedScheduledAutoPaymentUiV4 extends ScheduledAutoPaymentUiV4 {

  getAmountDisplay(..._: unknown[]): string | number {
    return 'Pending';
  }

  getDeclineRequest(store: Store): Observable<unknown> {
    return store.dispatch(new DeclineAutoPaymentV4(this.model.scheduledAutoPaymentId));
  }

  getResumeRequest(store: Store): Observable<unknown> {
    return store.dispatch(new ResumeAutoPaymentV4(this.model.scheduledAutoPaymentId));
  }
}
